import type { CapacitySettingInput } from '@/types/capacityImport';
import type { CapacityRemovalImpact, Vendor, VendorArchiveImpact } from '@/types/vendor';
import apiClient from './axios';

// Archived vendors included: the store keeps them for the Vendors list and PO Activity,
// and filters them out for pickers (activeVendors).
export async function fetchVendors(): Promise<Vendor[]> {
  const { data } = await apiClient.get<Vendor[]>('/vendors', { params: { includeArchived: true }, skipGlobalError: true });
  return data;
}

export async function fetchVendorById(id: string): Promise<Vendor> {
  const { data } = await apiClient.get<Vendor>(`/vendors/${id}`, { skipGlobalError: true });
  return data;
}

export async function createVendor(odooVendorId: number, alias: string | null): Promise<Vendor> {
  const { data } = await apiClient.post<Vendor>('/vendors', { odooVendorId, alias }, { skipGlobalError: true });
  return data;
}

export async function updateVendor(id: string, alias: string | null): Promise<Vendor> {
  const { data } = await apiClient.patch<Vendor>(`/vendors/${id}`, { alias }, { skipGlobalError: true });
  return data;
}

export async function setTransitDays(vendorId: string, transitDays: number): Promise<Vendor> {
  const { data } = await apiClient.patch<Vendor>(`/vendors/${vendorId}/transit-days`, { transitDays }, { skipGlobalError: true });
  return data;
}

export async function deleteVendor(id: string): Promise<void> {
  await apiClient.delete(`/vendors/${id}`, { skipGlobalError: true });
}

export async function fetchArchiveImpact(id: string): Promise<VendorArchiveImpact> {
  const { data } = await apiClient.get<VendorArchiveImpact>(`/vendors/${id}/archive-impact`, { skipGlobalError: true });
  return data;
}

export async function archiveVendor(id: string): Promise<Vendor> {
  const { data } = await apiClient.post<Vendor>(`/vendors/${id}/archive`, undefined, { skipGlobalError: true });
  return data;
}

export async function unarchiveVendor(id: string): Promise<Vendor> {
  const { data } = await apiClient.post<Vendor>(`/vendors/${id}/unarchive`, undefined, { skipGlobalError: true });
  return data;
}

export interface VendorCapacitySetting {
  id: string;
  capacityTypeId: string;
  capacityTypeCode: string;
  // Null means the vendor produces this type but no capacity factor is known for it yet
  // (e.g. auto-registered by an import whose sheet left that column blank).
  capacityFactor: number | null;
}

export async function fetchVendorCapacitySettings(vendorId: string): Promise<VendorCapacitySetting[]> {
  const { data } = await apiClient.get<VendorCapacitySetting[]>(`/vendors/${vendorId}/capacity-settings`, { skipGlobalError: true });
  return data;
}

export async function saveVendorCapacitySettings(vendorId: string, settings: CapacitySettingInput[]): Promise<void> {
  await apiClient.put(`/vendors/${vendorId}/capacity-settings`, { settings }, { skipGlobalError: true });
}

export async function deleteCapacityCalendarData(vendorId: string, capacityTypeCodes: string[]): Promise<void> {
  await apiClient.post(
    `/vendors/${vendorId}/capacity-settings/delete-calendar-data`,
    { capacityTypeCodes },
    { skipGlobalError: true },
  );
}

export async function fetchCapacityRemovalImpact(
  vendorId: string,
  capacityTypeCodes: string[],
): Promise<CapacityRemovalImpact[]> {
  const { data } = await apiClient.post<CapacityRemovalImpact[]>(
    `/vendors/${vendorId}/capacity-settings/removal-impact`,
    { capacityTypeCodes },
    { skipGlobalError: true },
  );
  return data;
}
